import {SaaSRole} from '../authz/rolesV1';
import {SystemRole} from '../authz/rolesV2';
import {Identity} from '../models/User';

export const CriblCloudUserProps = {
  Branding: 'https://cribl.cloud/branding',
  Company: 'https://cribl.cloud/company',
  CriblId: 'https://cribl.cloud/criblId',
  Email: 'https://cribl.cloud/email',
  Name: 'https://cribl.cloud/name',
  Organization: 'https://cribl.cloud/organization',
  OrganizationId: 'https://cribl.cloud/organizationId',
  Organizations: 'https://cribl.cloud/organizations',
  Role: 'https://cribl.cloud/role',
} as const;

export const OrganizationSystemRolesValues = [
  SystemRole.ADMIN_READER,
  SystemRole.ADMIN,
  SystemRole.SE,
  SystemRole.DEVELOPER,
];

export type OrganizationSystemRoles = (typeof OrganizationSystemRolesValues)[number];

const OEM_CONNECTION_PREFIX = 'OEM-';

export const getOEMProvider = (identities: Identity[]): string | null => {
  const provider = identities.find(
    (identity) => identity.provider === 'samlp' && identity.connection?.startsWith(OEM_CONNECTION_PREFIX),
  );

  return provider ? provider.connection : null;
};

export type MemberAccess = {
  orgAccess: {
    org_owner: boolean;
    org_user: boolean;
    org_admin: boolean;
    org_iam_admin: boolean;
  };
  workspaceAccess: {
    [key: string]: {
      ws_user: boolean;
      ws_admin: boolean;
      ws_owner: boolean;
    };
  };
  productAccess: {
    [key: string]: {
      edge: boolean;
      search: boolean;
      stream: boolean;
      lake: boolean;
    };
  };
};

export type MfaEnrollment = {
  id: string;
  status: string;
  enrolled_at: string;
  last_auth: string;
  type: string;
  auth_method: string;
  name: string;
};

export type UserInfo = {
  created?: string;
  company?: string;
  email: string;
  emailVerified: boolean;
  enabled: boolean;
  externalFlowComplete: string;
  familyName: string;
  givenName: string;
  name: string;
  phone?: string;
  picture: string;
  status: string;
  sub: string;
  updated: string;
  username: string;
  password: null;
  isOEM: boolean;
  memberAccess: MemberAccess;
  productAccess: {
    edge: boolean;
    search: boolean;
    stream: boolean;
  };
  roles: SaaSRole[];
  cloudRoles: string[];
  mfaEnrollment: MfaEnrollment[];
  courierUserHmac: string;
};

/**
 * @deprecated - This function is not a safe way to determine if a user has IDP-managed permissions. It does not
 *     properly account for linked accounts and only works in commercial. We need this in the short term because
 *     we do not always have the necessary information to determine this answer correctly and can temporarily
 *     deal with best-effort in the UI.
 *
 *     Backend validations MUST use the more reliable method of checking identities on an SSO user with
 *     SerializableUserModel.ssoProfiles */
export function hasIdpManagedPermissions(userId?: string) {
  if (!userId) {
    return false;
  }

  const parts = userId.split('|');
  if (parts.length !== 3) {
    return false;
  }
  const [provider, connection] = parts;

  return (connection !== 'CriblCorpOkta') &&
    (provider === 'samlp' || provider === 'oidc');
}

/**
 * @deprecated - This function only works for user IDs in the commercial environment and should be replaced with
 *     a method that works across Auth0 and Okta. We need this in the short term because we do not have enough
 *     information on the frontend to determine this accurately in FedRAMP
 */
export function hasIdpManagedProfile(userId?: string) {
  if (!userId) {
    return false;
  }

  const parts = userId.split('|');
  const provider = parts[0];

  return (provider === 'samlp' || provider === 'oidc' || provider === 'google-oauth2');
}

export function parseOemConnection(userId?: string) {
  if (!userId) {
    return null;
  }

  const parts = userId.split('|');
  const connection = parts[1];
  if (!connection || !connection.startsWith('OEM')) {
    return null;
  }

  return connection;
}
